<script setup lang="ts">
import { colorPalette as colors } from '@/utils/enums'
import { onMounted, onUnmounted, provide, ref } from 'vue'
import { RouterView } from 'vue-router'
import ApiErrorAssist from '@/components/ApiErrorAssist.vue'
import StuckAssist from '@/components/StuckAssist.vue'
import { sweepExpiredDrafts } from '@/composables/useInputDraft'

// レスポンシブ対応
const isMobile = ref(window.innerWidth <= 767)
const handleResize = () => {
  isMobile.value = window.innerWidth <= 767
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  sweepExpiredDrafts()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// 子コンポーネントで使えるようにprovide
provide('isMobile', isMobile)
</script>

<template>
  <RouterView />
  <ApiErrorAssist />
  <StuckAssist />
</template>
<style>
a {
  color: v-bind('colors.service.lighter');
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
}
a:visited {
  color: v-bind('colors.service.accent');
}
a:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* marked */
.md-text ul {
  margin: 0px 0px 16px;
  padding: 0px 0px 0px 26px;
}
.md-text ol {
  margin: 0px 0px 16px;
  padding: 0px 0px 0px 26px;
}
.md-text li {
  margin-block-start: 8px;
  margin-block-end: 8px;
  padding-inline-start: 6px;
  font-size: var(--font-size-body);
  line-height: var(--line-height-tight);
}
.md-text a {
  color: v-bind('colors.text.lighter2');
  cursor: pointer;
}
.md-text a:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}
.md-text h1 {
  height: auto;
  line-height: var(--line-height-loose);
  font-size: var(--font-size-title);
  margin-block-start: 40px;
  margin-block-end: 16px;
  font-weight: bold;
}
.md-text h2 {
  height: auto;
  line-height: var(--line-height-loose);
  font-size: var(--font-size-heading);
  margin-block-start: 32px;
  margin-block-end: 12px;
  font-weight: bold;
}
.md-text h3 {
  height: auto;
  line-height: var(--line-height-relaxed);
  font-size: var(--font-size-body-strong);
  margin-block-start: 24px;
  margin-block-end: 8px;
  font-weight: bold;
}
.md-text h4 {
  height: auto;
  line-height: var(--line-height-relaxed);
  font-size: var(--font-size-body);
  margin-block-start: 20px;
  margin-block-end: 8px;
  font-weight: bold;
}
.md-text p {
  font-size: var(--font-size-body);
  line-height: var(--line-height-normal);
  margin-block-start: 8px;
  margin-block-end: 8px;
}
.md-text strong {
  font-weight: bold;
}
.md-text table {
  border-collapse: collapse;
  border-spacing: 0;
  thead {
    text-align: left;
    border-bottom: 1px solid v-bind('colors.border.base');
    th {
      font-weight: bold;
    }
  }
  th,
  td {
    padding-right: 8px;
  }
}
.md-text hr {
  margin-block-start: 24px;
  margin-block-end: 24px;
}

/* Dialog Header */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Dialog Footer (split layout: destructive on left, confirm group on right) */
.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.dialog-footer-left,
.dialog-footer-right {
  display: flex;
  gap: 8px;
}

/* Element Plus button font-size overrides */
.el-button {
  font-size: var(--font-size-body);
}
.el-button.small {
  /* el-button自体のsizeはpaddingの話で、font-sizeを編集したい時は追加で.small classを付ける */
  font-size: var(--font-size-caption);
}

/* Element Plus input font-size overrides */
.el-input__inner,
.el-textarea__inner,
.el-select__placeholder,
.el-select__selected-item {
  font-size: var(--font-size-body);
}

/* Element Plus MessageBox font-size overrides */
.el-message-box__title {
  font-size: var(--font-size-body-strong);
}
.el-message-box__message {
  font-size: var(--font-size-body);
  line-height: var(--line-height-normal);
}

/* API のエラーメッセージは複数の理由を改行でつないで返す */
.mm-message .el-message__content {
  white-space: pre-line;
}

/* _ElConfirm: el-button circle 風の close ボタンに揃える */
.mm-confirm .el-message-box__headerbtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--el-border-color);
  border-radius: 50%;
  background-color: var(--el-button-bg-color);
}
.mm-confirm .el-message-box__headerbtn:hover,
.mm-confirm .el-message-box__headerbtn:focus {
  border-color: var(--el-color-primary);
  background-color: var(--el-color-primary-light-9);
}
.mm-confirm .el-message-box__close {
  font-size: 14px;
  color: var(--el-text-color-regular);
}
.mm-confirm .el-message-box__headerbtn:hover .el-message-box__close,
.mm-confirm .el-message-box__headerbtn:focus .el-message-box__close {
  color: var(--el-color-primary);
}
</style>
